import { buildPayPalSdkUrl } from '@dukanti/shared';

type PayPalButtonsInstance = {
  render: (selector: string | HTMLElement) => Promise<void>;
};

export type PayPalSubscriptionActions = {
  subscription: {
    create(input: { plan_id: string; custom_id?: string }): Promise<string>;
  };
};

export type PayPalApproveData = {
  subscriptionID?: string;
};

export type PayPalNamespace = {
  Buttons(input: {
    createSubscription: (data: unknown, actions: PayPalSubscriptionActions) => Promise<string>;
    onApprove: (data: PayPalApproveData) => void | Promise<void>;
    onCancel?: (data: unknown) => void;
    onError: (error: unknown) => void;
    style?: {
      color?: 'gold' | 'blue' | 'silver' | 'white' | 'black';
      label?: 'paypal' | 'checkout' | 'buynow' | 'pay' | 'installment' | 'subscribe' | 'donate';
      layout?: 'vertical' | 'horizontal';
      shape?: 'rect' | 'pill' | 'sharp';
      tagline?: boolean;
    };
  }): PayPalButtonsInstance;
};

declare global {
  interface Window {
    paypal?: PayPalNamespace;
  }
}

let sdkPromise: Promise<PayPalNamespace> | null = null;
let activeSdkUrl: string | null = null;
let rejectSdkPromise: ((reason?: unknown) => void) | null = null;

function clearPayPalSdk(): void {
  document
    .querySelectorAll<HTMLScriptElement>('[data-testid="paypal-sdk-script"]')
    .forEach((script) => {
      script.remove();
    });
  rejectSdkPromise?.(new Error('PayPal SDK reload requested'));
  delete window.paypal;
  sdkPromise = null;
  activeSdkUrl = null;
  rejectSdkPromise = null;
}

export function loadPayPalSdk(input: {
  readonly clientId: string;
  readonly locale?: string | null;
}): Promise<PayPalNamespace> {
  const sdkUrl = buildPayPalSdkUrl({
    clientId: input.clientId,
    currency: 'ILS',
    locale: input.locale,
  });

  if (window.paypal && activeSdkUrl === sdkUrl) return Promise.resolve(window.paypal);
  if (sdkPromise && activeSdkUrl === sdkUrl) return sdkPromise;
  if (activeSdkUrl && activeSdkUrl !== sdkUrl) clearPayPalSdk();
  if (window.paypal && !activeSdkUrl) return Promise.resolve(window.paypal);

  activeSdkUrl = sdkUrl;
  sdkPromise = new Promise((resolve, reject) => {
    rejectSdkPromise = reject;
    const script = document.createElement('script');
    script.src = sdkUrl;
    script.async = true;
    script.dataset.testid = 'paypal-sdk-script';
    script.dataset.sdkUrl = sdkUrl;
    script.onload = () => {
      if (window.paypal) {
        rejectSdkPromise = null;
        resolve(window.paypal);
        return;
      }
      if (activeSdkUrl === sdkUrl) {
        sdkPromise = null;
        activeSdkUrl = null;
        rejectSdkPromise = null;
      }
      reject(new Error('PayPal SDK did not initialize'));
    };
    script.onerror = () => {
      if (activeSdkUrl === sdkUrl) {
        sdkPromise = null;
        activeSdkUrl = null;
        rejectSdkPromise = null;
      }
      reject(new Error('PayPal SDK failed to load'));
    };
    document.head.appendChild(script);
  });

  return sdkPromise;
}
